/**
 * searchCommands — the pure mapping from a global-search "config" hit to a command-palette row.
 *
 * Extracted OUT of CommandPalette.tsx on purpose: this is the one piece of the palette's config
 * lane with real branching (kind -> route/label), so it lives here as a side-effect-free function
 * the Node test runner can import from a plain `.ts` file (no React/JSX harness needed). The
 * component keeps the navigation side effects (setOrg + navigate); this only decides WHERE to go.
 */
import type { SearchConfigHit } from "@/types/domain";

/**
 * Catalog kind (backend CatalogKindEnum.value, or "template") -> the org-level section route
 * segment it deep-links to. NETWORK is intentionally absent: it has no landing screen, and the
 * backend never emits it in the configs lane. A hit whose kind is not here is dropped (below),
 * so we can never build a "/o/1/undefined" route.
 */
export const KIND_TO_ROUTE: Record<string, string> = {
  ssid: "ssids",
  radius: "radius-servers",
  acl: "access-control",
  firewall_policy: "firewall",
  radio_profile: "radio",
  port_profile: "switch-portprofiles",
  template: "templates",
};

/** Catalog kind -> the short human label shown in the row hint ("Config · {label}"). */
export const KIND_TO_LABEL: Record<string, string> = {
  ssid: "SSID",
  radius: "RADIUS",
  acl: "ACL",
  firewall_policy: "Firewall",
  radio_profile: "Radio",
  port_profile: "Port profile",
  template: "Template",
};

/** A palette-ready descriptor for a config hit: everything the row needs EXCEPT the side effect.
 *  `orgId` + `to` are handed to the component's jump() so it can switch org scope then navigate. */
export interface ConfigCommandDescriptor {
  id: string;
  label: string;
  hint: string;
  orgId: number;
  to: string;
}

/** Map ONE config hit to its descriptor. Pure: no navigation, no store access. */
export function configHitToCommand(hit: SearchConfigHit): ConfigCommandDescriptor {
  const route = KIND_TO_ROUTE[hit.kind];
  const label = KIND_TO_LABEL[hit.kind] ?? hit.kind;
  return {
    // Stable, collision-free id: kind namespaces the id space so an SSID #3 and a Radio #3
    // never share a key.
    id: `cfg-${hit.kind}-${hit.id}`,
    // Surface the backend's detail (e.g. a RADIUS host:port) alongside the name when present,
    // mirroring how a client hit shows "hostname · mac".
    label: hit.detail ? `${hit.name} · ${hit.detail}` : hit.name,
    hint: `Config · ${label}`,
    orgId: hit.org_id,
    to: `/o/${hit.org_id}/${route}`,
  };
}

/**
 * Map a list of config hits to descriptors, dropping any kind we have no route for (defensive:
 * a future backend kind must not render an unclickable/404 row). Order is preserved — the
 * backend already merged + name-sorted the lane.
 */
export function configHitsToCommands(hits: SearchConfigHit[]): ConfigCommandDescriptor[] {
  return hits.filter((h) => h.kind in KIND_TO_ROUTE).map(configHitToCommand);
}
